/**
 * ProcessControlBar — единая панель управления для /process/* (Search, Generation, Matching).
 *
 * P1.4 (2026-08-14) — редизайн в явный CSS-grid из 4 секций
 * (согласовано с Janni: «4 секции в едином баре», UX-J3 — НЕ раздельные карточки
 * вне бара; здесь секции — ячейки единого grid-бара):
 *
 *   1. Датасет       — имя (line-clamp:2, break-words) + выбор + встроенный
 *                      прогресс-бар статуса задачи (usePipelineStatus).
 *   2. Тариф + кредиты — segmented Эконом/Оптимал/Макс + cost one-liner
 *                      (estimate до запуска; факт после — в detail drawer / P1.3)
 *                      + available balance. Language (Generation) — тут же.
 *   3. Кнопки        — Назад (navigate(-1)) / Скачать Excel (клиентский .xlsx,
 *                      SheetJS, без BE-эндпоинта).
 *   4. Строки        — 1 / 3 / 5 / All (тестовая выборка, «All» локализован)
 *                      + «Запустить» (Start/Pause/Resume/Cancel) под ними.
 *
 * P1.4: секция «Библиотека» и акцент «Save» убраны — единая workspace-библиотека
 * теперь живёт в Header (WorkspaceLibraryBar), применяется ко всем вкладкам сразу.
 *
 * Responsive: xl (≥1280) — 4 колонки в один ряд; lg — 3 колонки (≤2 ряда);
 * sm — 2 колонки; mobile — 1 колонка. Никогда не 5 карточек.
 *
 * Запреты:
 * - НЕ показывать бренды провайдеров (Perplexity/SerpAPI) на user-панелях (UX-J2).
 * - Все строки — через i18n (ru/ua/en).
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Database,
  Coins,
  Play,
  Pause,
  Square,
  Loader2,
  Globe,
  ListChecks,
  Crown,
  ArrowLeft,
  Download,
} from 'lucide-react';

import { useProjectDatasets } from '@/hooks/useFiles';
import { useProjectStore } from '@/store/projectStore';
import { useMyBalance } from '@/hooks/useAccount';
import { cn } from '@/lib/utils';

export type TierKey = 'economy' | 'optimal' | 'max';

export interface ProcessControlBarProps {
  /** Текущий dataset id (синхронизирован со store страницы). */
  datasetId: string | null;
  setDatasetId: (id: string | null) => void;
  /** Текущий tier. */
  tierKey: TierKey;
  setTierKey: (t: TierKey) => void;
  /** Output language (только для Generation; для Search — null). */
  outputLanguage?: string;
  setOutputLanguage?: (lang: string) => void;
  /** Row scope (1 / 3 / 5 / All). */
  testRunLimit: number;
  setTestRunLimit: (n: number) => void;
  isTestRun: boolean;
  setIsTestRun: (v: boolean) => void;
  /** Cost estimate one-liner — передаётся снаружи (из estimate-hook). */
  costEstimate?: string | null;
  /** Execution state (от ExecutionPanel-логики страницы). */
  execution: ProcessExecutionState;
  /**
   * P1.1: client-side .xlsx export of the visible result rows. When omitted
   * (e.g. a module without a results table yet), the «Скачать Excel» button is
   * not rendered. The page owns the row→flat-object mapping (UX-J2: no
   * provider brands leak); see lib/exportExcel.ts.
   */
  onExportExcel?: () => void;
}

export interface ProcessExecutionState {
  status: 'idle' | 'queued' | 'running' | 'paused' | 'completed' | 'cancelled' | 'failed';
  isStarting: boolean;
  canStart: boolean;
  startLabel: string;
  onStart: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  pausePending?: boolean;
  resumePending?: boolean;
  cancelPending?: boolean;
  progress?: { processed: number; total: number };
  /** Fix 2026-09-18: test run — прогресс описывает scope прогона, не датасет. */
  isTestRun?: boolean;
  /** Полный размер датасета (из /search/results total_rows) — для подписи «в датасете N строк». */
  datasetTotal?: number;
  errorMessage?: string | null;
}

const TIER_OPTIONS: { key: TierKey; labelKey: string }[] = [
  { key: 'economy', labelKey: 'economy' },
  { key: 'optimal', labelKey: 'optimal' },
  { key: 'max', labelKey: 'max' },
];

// M1 (2026-09-10): лимит тест-прогона — default 2, максимум 3 (backend le=3).
const ROW_SCOPE_OPTIONS: { value: number | 'all' }[] = [
  { value: 2 },
  { value: 3 },
  { value: 'all' },
];

const LANGUAGES: { code: string; label: string }[] = [
  { code: 'auto', label: 'Auto' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pl', label: 'Polski' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'tr', label: 'Türkçe' },
];

export function ProcessControlBar(props: ProcessControlBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    datasetId,
    setDatasetId,
    tierKey,
    setTierKey,
    outputLanguage,
    setOutputLanguage,
    testRunLimit,
    setTestRunLimit,
    isTestRun,
    setIsTestRun,
    costEstimate,
    execution,
    onExportExcel,
  } = props;

  const storeProjectId = useProjectStore((s) => s.currentProjectId);
  const { data } = useProjectDatasets(storeProjectId);
  const datasets = (data?.items ?? []).filter((d) => d.status === 'ready');
  const { data: balance } = useMyBalance();
  const available = balance ? parseFloat(balance.available_balance) : 0;
  const selectedDataset = datasets.find((d) => d.id === datasetId) ?? null;

  const hasProgress =
    !!execution.progress && execution.progress.total > 0 && execution.status !== 'idle';

  return (
    <div className="space-y-3">
      {/* === Единый бар: 4 секции в явном CSS-grid === */}
      <div className="card grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 px-3 py-3">
        {/* --- Section 1: Датасет + встроенный прогресс --- */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <SectionLabel icon={Database} text={t('process.bar.dataset', { defaultValue: 'Dataset' })} />
          <div
            className="text-sm font-medium break-words line-clamp-2 min-h-[2.5rem]"
            title={selectedDataset?.name ?? ''}
          >
            {selectedDataset ? (
              <>
                {selectedDataset.name}
                <span className="text-surface-500 font-normal">
                  {' '}({selectedDataset.total_rows})
                </span>
              </>
            ) : (
              <span className="text-surface-500">
                {t('process.bar.datasetChoose', { defaultValue: '— Dataset —' })}
              </span>
            )}
          </div>
          <select
            className="input py-1 text-sm w-full"
            value={datasetId ?? ''}
            onChange={(e) => setDatasetId(e.target.value || null)}
            aria-label={t('process.bar.dataset', { defaultValue: 'Dataset' })}
          >
            <option value="">
              {t('process.bar.datasetChoose', { defaultValue: '— Dataset —' })}
            </option>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.total_rows})
              </option>
            ))}
          </select>
          {hasProgress && (
            <ProgressBar
              processed={execution.progress!.processed}
              total={execution.progress!.total}
              status={execution.status}
              compact
              isTestRun={execution.isTestRun}
              datasetTotal={execution.datasetTotal}
            />
          )}
        </div>

        {/* --- Section 2: Тариф + кредиты (+ Language для Generation) --- */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <SectionLabel
            icon={Crown}
            text={t('process.bar.tierLabel', { defaultValue: 'Tier & credits' })}
          />
          <div className="flex items-center rounded-lg border border-surface-700 overflow-hidden text-xs w-fit">
            {TIER_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setTierKey(opt.key)}
                title={t(`process.bar.tier.hint.${opt.labelKey}`, {
                  defaultValue: t('process.bar.tier.hint', {
                    defaultValue: 'Tier affects cost and depth, not guarantee of quality.',
                  }),
                })}
                className={cn(
                  'px-2.5 py-1 transition-colors',
                  tierKey === opt.key
                    ? 'bg-primary-500/20 text-primary-200'
                    : 'bg-surface-900 text-surface-400 hover:text-surface-200',
                )}
              >
                {t(`process.bar.tier.${opt.labelKey}`, { defaultValue: opt.key })}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-surface-400 min-w-0">
            <Coins className="w-4 h-4 text-warning shrink-0" />
            <span className="font-mono truncate" title={costEstimate ?? ''}>
              {costEstimate ?? t('process.bar.costPlaceholder', { defaultValue: '— credits' })}
            </span>
            <span className="text-surface-500 hidden xl:inline">
              ({Math.round(available).toLocaleString()} {t('process.bar.avail', { defaultValue: 'avail.' })})
            </span>
          </div>
          {setOutputLanguage && outputLanguage !== undefined && (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-surface-400 shrink-0" />
              <select
                className="input py-1 text-sm w-full"
                value={outputLanguage}
                onChange={(e) => setOutputLanguage(e.target.value)}
                aria-label={t('process.bar.language', { defaultValue: 'Output language' })}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* --- Section 3: Кнопки (Назад / Excel) --- */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <SectionLabel
            icon={ArrowLeft}
            text={t('process.bar.actions', { defaultValue: 'Actions' })}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs"
              title={t('process.bar.back', { defaultValue: 'Back' })}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('process.bar.back', { defaultValue: 'Back' })}
            </button>
            {onExportExcel && (
              <button
                type="button"
                onClick={onExportExcel}
                className="btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs"
                title={t('process.bar.exportTitle', {
                  defaultValue: 'Download visible rows as .xlsx',
                })}
              >
                <Download className="w-3.5 h-3.5" />
                {t('process.bar.export', { defaultValue: 'Excel' })}
              </button>
            )}
          </div>
        </div>

        {/* --- Section 4: Строки + Запустить --- */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <SectionLabel
            icon={ListChecks}
            text={t('process.bar.scope', { defaultValue: 'Rows' })}
          />
          <div className="flex items-center rounded-lg border border-surface-700 overflow-hidden text-xs w-fit">
            {ROW_SCOPE_OPTIONS.map((opt) => {
              const active = isTestRun ? opt.value === testRunLimit : opt.value === 'all';
              const label =
                opt.value === 'all'
                  ? t('process.bar.scopeAll', { defaultValue: 'All' })
                  : String(opt.value);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  data-testid={`scope-${opt.value}`}
                  onClick={() => {
                    if (opt.value === 'all') {
                      setIsTestRun(false);
                    } else {
                      setIsTestRun(true);
                      setTestRunLimit(opt.value as number);
                    }
                  }}
                  className={cn(
                    'px-2.5 py-1 transition-colors',
                    active
                      ? 'bg-primary-500/20 text-primary-200'
                      : 'bg-surface-900 text-surface-400 hover:text-surface-200',
                  )}
                  title={
                    opt.value === 'all'
                      ? t('process.bar.scopeAllTitle', { defaultValue: 'Process all rows' })
                      : t('process.bar.scopeTestTitle', {
                          defaultValue: `Process first ${opt.value} random rows`,
                          n: opt.value,
                        })
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
          <ExecutionSlot execution={execution} />
        </div>
      </div>

      {/* Progress full-width when running AND it didn't fit in section 1 (narrow) */}
      {hasProgress && (
        <ProgressBar
          processed={execution.progress!.processed}
          total={execution.progress!.total}
          status={execution.status}
          isTestRun={execution.isTestRun}
          datasetTotal={execution.datasetTotal}
        />
      )}

      {execution.errorMessage && (
        <div className="text-xs text-danger bg-danger-tint/10 border border-danger-tint/20 rounded-lg px-3 py-2">
          {execution.errorMessage}
        </div>
      )}
    </div>
  );
}

// === Internal: section label (icon + uppercase tiny label) ===

function SectionLabel({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-surface-500 uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{text}</span>
    </div>
  );
}

// === Internal: Execution slot — primary Start + pause/cancel ===

function ExecutionSlot({ execution }: { execution: ProcessExecutionState }) {
  const { t } = useTranslation();
  const { status, isStarting, canStart, startLabel, onStart } = execution;
  const isRunning = status === 'running' || status === 'queued';
  const isPaused = status === 'paused';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        data-testid="start-run"
        onClick={onStart}
        disabled={!canStart || isRunning}
        className={cn(
          'flex shrink-0 items-center justify-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
          'bg-primary-600 hover:bg-primary-500 text-white',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {isStarting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Play className="w-4 h-4" />
        )}
        {startLabel ?? t('process.bar.start', { defaultValue: 'Start' })}
      </button>

      {isRunning && execution.onPause && !isPaused && (
        <button
          type="button"
          onClick={execution.onPause}
          disabled={execution.pausePending}
          className="btn-secondary flex items-center gap-1 px-2.5 py-1.5 text-xs"
          title={t('process.bar.pause', { defaultValue: 'Pause' })}
        >
          <Pause className="w-3.5 h-3.5" />
        </button>
      )}
      {isPaused && execution.onResume && (
        <button
          type="button"
          onClick={execution.onResume}
          disabled={execution.resumePending}
          className="btn-secondary flex items-center gap-1 px-2.5 py-1.5 text-xs"
          title={t('process.bar.resume', { defaultValue: 'Resume' })}
        >
          <Play className="w-3.5 h-3.5" />
        </button>
      )}
      {isRunning && execution.onCancel && (
        <button
          type="button"
          onClick={execution.onCancel}
          disabled={execution.cancelPending}
          className="btn-danger flex items-center gap-1 px-2.5 py-1.5 text-xs"
          title={t('process.bar.cancel', { defaultValue: 'Cancel' })}
        >
          <Square className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// === Internal: ProgressBar — compact (inside section 1) or full-width ===

function ProgressBar({
  processed,
  total,
  status,
  compact = false,
  isTestRun = false,
  datasetTotal,
}: {
  processed: number;
  total: number;
  status: ProcessExecutionState['status'];
  compact?: boolean;
  isTestRun?: boolean;
  datasetTotal?: number;
}) {
  const { t } = useTranslation();
  const percent = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  // Fix 2026-09-18: test run — scope прогона ≠ датасет; подпись различает их,
  // чтобы «2/2» не читалось как «весь датасет обработан».
  const isTestRunProgress =
    isTestRun && datasetTotal !== undefined && datasetTotal > 0 && datasetTotal !== total;
  const testRunCaption = isTestRunProgress
    ? t('process.bar.testRunProgress', {
        processed,
        total,
        datasetTotal,
        defaultValue: `Test run: ${processed} of ${total} · dataset has ${datasetTotal} rows`,
      })
    : null;
  const color =
    status === 'completed'
      ? 'bg-success-tint'
      : status === 'failed'
        ? 'bg-danger-tint'
        : 'bg-primary-500';
  if (compact) {
    return (
      <div className="flex items-center gap-2" title={testRunCaption ?? undefined}>
        <div className="flex-1 h-1.5 bg-surface-800 rounded-full overflow-hidden min-w-[4rem]">
          <div
            className={cn('h-full transition-all duration-500 rounded-full', color)}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="text-[11px] text-surface-400 font-mono tabular-nums shrink-0">
          {processed}/{total}
        </span>
      </div>
    );
  }
  return (
    <div className="card flex items-center gap-3 py-2">
      <div className="text-xs text-surface-400 font-mono tabular-nums whitespace-nowrap">
        {isTestRunProgress ? (
          <>
            {t('process.bar.testRunPrefix', { defaultValue: 'Test run' })}
            {': '}
          </>
        ) : null}
        {processed} / {total}
        {datasetTotal !== undefined && datasetTotal !== total
          ? ` · ${t('process.bar.datasetRows', {
              datasetTotal,
              defaultValue: `dataset: ${datasetTotal} rows`,
            })}`
          : ''}
        {` (${Math.round(percent)}%)`}
      </div>
      <div className="flex-1 h-2 bg-surface-800 rounded-full overflow-hidden">
        <div
          className={cn('h-full transition-all duration-500 rounded-full', color)}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="text-xs uppercase tracking-wider text-surface-500">
        {t(`process.bar.status.${status}`, { defaultValue: status })}
      </div>
    </div>
  );
}